import React, { useEffect, useRef, useState } from 'react';
import { getProfessionalPhotoThumbUrls, getProfessionalPhotoUrl } from '@/services/schedulingService';

// Presigned URLs: full photo valid 5 min; thumbnails valid >=10 min after the
// server hands them out (it reuses the same URL so the browser can cache it).
const FULL_TTL = 4 * 60 * 1000;
const THUMB_TTL = 9 * 60 * 1000;
const THUMB_MAX_SIZE = 32; // thumbnails are 96px — fine up to 32px displayed

const cache = new Map();
function cached(cacheKey, ttl, load) {
  const hit = cache.get(cacheKey);
  if (hit && hit.expires > Date.now()) return hit.promise;
  const promise = load().catch(() => null);
  cache.set(cacheKey, { promise, expires: Date.now() + ttl });
  return promise;
}

const fullPhotoFor = (id, key) => cached(`full:${id}:${key}`, FULL_TTL, () => getProfessionalPhotoUrl(id));

// Every thumbnail asked for in the same ~20ms window goes out in one request,
// so a page of tables costs one round trip instead of one per professional.
let pending = new Map();
let timer = null;
function flushThumbs() {
  const batch = pending;
  pending = new Map();
  timer = null;
  getProfessionalPhotoThumbUrls([...batch.keys()])
    .then(urls => batch.forEach((resolve, id) => resolve(urls[id] || null)))
    .catch(() => batch.forEach(resolve => resolve(null)));
}
const thumbFor = (id, key) =>
  cached(`thumb:${id}:${key}`, THUMB_TTL, () =>
    new Promise(resolve => {
      pending.set(id, resolve);
      if (!timer) timer = setTimeout(flushThumbs, 20);
    })
  );

export default function ProfessionalPhoto({ professional, size = 24 }) {
  const ref = useRef(null);
  const [url, setUrl] = useState(null);
  const [useFull, setUseFull] = useState(size > THUMB_MAX_SIZE);
  const [failed, setFailed] = useState(false);
  const { ProfessionalID: id, PhotoKey: key, ProfessionalName: name, area } = professional;
  useEffect(() => {
    setUseFull(size > THUMB_MAX_SIZE);
  }, [id, key, size]);
  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setFailed(false);
    if (!key || (area || '').toLowerCase() === 'contractor') return;
    // Start loading well before the row scrolls into view so photos are
    // already there when the next week's table appears.
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          observer.disconnect();
          (useFull ? fullPhotoFor(id, key) : thumbFor(id, key)).then(value => {
            if (cancelled) return;
            if (value) setUrl(value);
            else if (!useFull) setUseFull(true);
          });
        }
      },
      { rootMargin: '2000px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [id, key, area, useFull]);
  // A missing thumbnail falls back to the full-size photo before giving up.
  const handleError = () => (useFull ? setFailed(true) : setUseFull(true));
  const words = (name || '?').trim().split(/\s+/);
  const initials = `${words[0]?.[0] || '?'}${words.length > 1 ? words[words.length - 1][0] : ''}`.toUpperCase();
  return (
    <span ref={ref} className='professional-photo' style={{ width: size, height: size }} aria-hidden='true'>
      {url && !failed ? <img src={url} alt='' onError={handleError} decoding='async' /> : initials}
    </span>
  );
}

export function ProfessionalName({ professional }) {
  return (
    <span className='professional-name' title={professional.ProfessionalName}>
      <ProfessionalPhoto professional={professional} />
      <span className='professional-name-text'>{professional.ProfessionalName}</span>
    </span>
  );
}
